import { useRef } from 'react';
import type { ProjectTimeline } from '@/types';

interface TimelineProps {
  timeline: ProjectTimeline;
  currentTime: number;
  isPlaying: boolean;
  onSeek: (time: number) => void;
  onTogglePlay: () => void;
  onStep: (deltaSeconds: number) => void;
}

const TRACK_ORDER = ['image', 'voiceover', 'caption', 'music', 'watermark'] as const;
const TRACK_LABELS: Record<(typeof TRACK_ORDER)[number], string> = {
  image: 'Images',
  voiceover: 'Voiceover',
  caption: 'Captions',
  music: 'Music',
  watermark: 'Watermark',
};
const ROW_HEIGHT = 24;

export function Timeline({ timeline, currentTime, isPlaying, onSeek, onTogglePlay, onStep }: TimelineProps) {
  const trackAreaRef = useRef<HTMLDivElement>(null);
  const duration = Math.max(0.1, timeline.totalDurationSeconds);

  function timeFromClientX(clientX: number): number {
    const el = trackAreaRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return ratio * duration;
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-3 flex items-center gap-2">
        <button onClick={() => onStep(-5)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" title="Back 5s" aria-label="Step back 5 seconds">
          ⏮
        </button>
        <button onClick={onTogglePlay} className="rounded-full bg-brand-600 p-2 text-white hover:bg-brand-700" title={isPlaying ? 'Pause' : 'Play'} aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? '⏸' : '▶'}
        </button>
        <button onClick={() => onStep(5)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" title="Forward 5s" aria-label="Step forward 5 seconds">
          ⏭
        </button>
        <span className="ml-2 font-mono text-xs text-slate-500 dark:text-slate-400">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>

      <div className="flex gap-2">
        <div className="flex shrink-0 flex-col gap-1" style={{ width: '4rem' }}>
          {TRACK_ORDER.map((track) => (
            <div key={track} style={{ height: ROW_HEIGHT }} className="flex items-center justify-end text-[10px] uppercase tracking-wide text-slate-400">
              {TRACK_LABELS[track]}
            </div>
          ))}
        </div>

        <div
          ref={trackAreaRef}
          onClick={(e) => onSeek(timeFromClientX(e.clientX))}
          className="relative flex-1 cursor-pointer select-none rounded bg-slate-50 dark:bg-slate-800/50"
          style={{ height: ROW_HEIGHT * TRACK_ORDER.length + (TRACK_ORDER.length - 1) * 4 }}
        >
          {TRACK_ORDER.map((track, rowIndex) => {
            const items = timeline.items.filter((i) => i.track === track);
            return (
              <div key={track} className="absolute left-0 right-0 rounded bg-slate-100 dark:bg-slate-800" style={{ top: rowIndex * (ROW_HEIGHT + 4), height: ROW_HEIGHT }}>
                {items.map((item) => (
                  <div
                    key={item.id}
                    title={`${item.label}: ${formatTime(item.startSeconds)}–${formatTime(item.endSeconds)}`}
                    className="absolute top-0.5 h-[calc(100%-4px)] rounded-sm opacity-80"
                    style={{
                      left: `${(item.startSeconds / duration) * 100}%`,
                      width: `${Math.max(0.5, ((item.endSeconds - item.startSeconds) / duration) * 100)}%`,
                      backgroundColor: item.color,
                    }}
                  />
                ))}
              </div>
            );
          })}

          <div className="pointer-events-none absolute top-0 z-10 h-full w-0.5 bg-red-500" style={{ left: `${(currentTime / duration) * 100}%` }} />
        </div>
      </div>
    </div>
  );
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = (seconds % 60).toFixed(1).padStart(4, '0');
  return `${m}:${s}`;
}
